import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import {
  Button,
  Checkbox,
  Chip,
  PopoverDialog,
  SearchField,
  SpinningLoader,
  Text,
  UserIcon,
  useControlledField,
  usePopoverDialog,
} from '@frontend/design-system';
import {
  ACTOR_SYSTEM_OPTION,
  ACTOR_WEAVE_OPTION,
  USER_FILTER_OFFICE_STAFF_LIMIT,
  WEAVE_USER_LABEL,
} from './phone-system-history-config';
import { PhoneSystemHistoryTrackingIds } from './utils/tracking-ids';

type UserShape = {
  userId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
};

type UserFilterProps = {
  value: string[];
  users: UserShape[];
  usersIsLoading: boolean;
  onApply: (value: string[]) => void;
  onDismiss: () => void;
  draftResetKey: number;
};

export function UserFilter({ value, users, usersIsLoading, onApply, onDismiss, draftResetKey }: UserFilterProps) {
  const { t } = useTranslation('phone');
  // draftValue is the local state of the filter selections inside the popover. It is only committed to the parent via
  // onApply when the user clicks "Apply". This allows users to make multiple selections before committing and allows
  // us to reset the draft state when the user closes the popover without applying changes.
  const [draftValue, setDraftValue] = useState<string[]>(value);
  const [searchTerm, setSearchTerm] = useState('');

  // Reset draft state when the committed value changes externally (e.g. "Clear All" pressed).
  useEffect(() => {
    setDraftValue(value);
  }, [draftResetKey, value]);

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchTerm,
    onChange: (val) => setSearchTerm(val as string),
  });

  const { getTriggerProps, getDialogProps, close } = usePopoverDialog({
    placement: 'bottom-start',
    onClose: () => {
      setSearchTerm('');
      setDraftValue(value);
      onDismiss();
    },
  });

  // --- Derived state ---

  // O(1) set for isChecked / isDisabled lookups — avoids O(n) array scan per row.
  const draftValueSet = useMemo(() => new Set(draftValue), [draftValue]);

  const isWeaveSystemSelected = draftValueSet.has(ACTOR_SYSTEM_OPTION);
  const isWeaveUserSelected = draftValueSet.has(ACTOR_WEAVE_OPTION);

  const selectedOfficeStaffCount = useMemo(
    () => draftValue.filter((v) => v !== ACTOR_SYSTEM_OPTION && v !== ACTOR_WEAVE_OPTION).length,
    [draftValue]
  );
  const isAtLimit = selectedOfficeStaffCount >= USER_FILTER_OFFICE_STAFF_LIMIT;

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return users;
    return users.filter((u) => {
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').toLowerCase();
      return fullName.includes(term);
    });
  }, [users, searchTerm]);

  const isFilterActive = value.length > 0;

  // --- Stable toggle handlers ---
  // Use functional updater so no state values need to be in the dependency array,
  // keeping these callbacks stable across re-renders.

  const toggleWeaveRelatedType = useCallback((val: string) => {
    setDraftValue((prev) => (prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]));
  }, []);

  // Office-staff toggle — enforces the limit inside the updater to avoid stale closure issues.
  const toggleUserId = useCallback((userId: string) => {
    setDraftValue((prev) => {
      const isAlreadySelected = prev.includes(userId);
      const officeStaffCount = prev.filter((v) => v !== ACTOR_SYSTEM_OPTION && v !== ACTOR_WEAVE_OPTION).length;

      if (!isAlreadySelected && officeStaffCount >= USER_FILTER_OFFICE_STAFF_LIMIT) return prev;

      return isAlreadySelected ? prev.filter((v) => v !== userId) : [...prev, userId];
    });
  }, []);

  // Stable callbacks so the two Weave checkbox rows also benefit from React.memo.
  const toggleWeaveSystem = useCallback(() => toggleWeaveRelatedType(ACTOR_SYSTEM_OPTION), [toggleWeaveRelatedType]);
  const toggleWeaveUser = useCallback(() => toggleWeaveRelatedType(ACTOR_WEAVE_OPTION), [toggleWeaveRelatedType]);

  const handleClear = useCallback(() => setDraftValue([]), []);

  const handleApply = () => {
    onApply(draftValue);
    close();
  };

  const officeStaffLabel =
    selectedOfficeStaffCount > 0
      ? t('Office Staff ({{count}}/{{limit}})', {
          count: selectedOfficeStaffCount,
          limit: USER_FILTER_OFFICE_STAFF_LIMIT,
        })
      : t('Office Staff');

  return (
    <>
      <Chip.DropdownFilter
        {...getTriggerProps()}
        filterIsActive={isFilterActive}
        leftElement={<UserIcon size={16} css={{ flexShrink: 0 }} />}
        css={{ maxWidth: 'none' }}
        trackingId={PhoneSystemHistoryTrackingIds.tableFilters.userDropdown}
      >
        {t('User')}
      </Chip.DropdownFilter>

      <PopoverDialog {...getDialogProps()} css={userFilterDialogStyle}>
        {/* Search — filters office staff list only */}
        <div css={searchSectionStyle}>
          <SearchField
            {...searchFieldProps}
            name='user-filter-search'
            label={t('Search users')}
            placeholder={t('Search users')}
          />
        </div>

        {/* Scrollable body */}
        <div css={scrollableBodyStyle}>
          {/* Hardcoded Weave actor type options (always visible, not affected by search) */}
          <div css={weaveOptionsStyle}>
            <OptionCheckboxRow
              name='user-filter-weave-system'
              label={t('Weave System')}
              isChecked={isWeaveSystemSelected}
              trackingId={PhoneSystemHistoryTrackingIds.tableFilters.userWeaveSystemCheckbox}
              onToggle={toggleWeaveSystem}
            />
            <OptionCheckboxRow
              name='user-filter-weave-user'
              label={WEAVE_USER_LABEL}
              isChecked={isWeaveUserSelected}
              trackingId={PhoneSystemHistoryTrackingIds.tableFilters.userWeaveTeamCheckbox}
              onToggle={toggleWeaveUser}
            />
          </div>

          {/* Divider between Weave options and office staff */}
          <div css={dividerStyle} />

          {/* Office Staff section header with selection counter */}
          <div css={officeStaffHeaderStyle}>
            <Text weight='bold' size='medium'>
              {officeStaffLabel}
            </Text>
          </div>

          {/* Office staff checkboxes */}
          <div css={officeStaffCheckboxesStyle}>
            {usersIsLoading ? (
              <SpinningLoader size='xs' />
            ) : filteredUsers.length === 0 ? (
              <Text size='medium' color='light'>
                {t('No users found')}
              </Text>
            ) : (
              filteredUsers.map((user) => {
                const userId = user.userId ?? '';
                const isChecked = draftValueSet.has(userId);
                const isDisabled = isAtLimit && !isChecked;
                const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
                return (
                  <UserCheckboxRow
                    key={userId}
                    name={`user-filter-${userId}`}
                    label={fullName}
                    userId={userId}
                    isChecked={isChecked}
                    isDisabled={isDisabled}
                    toggleUserId={toggleUserId}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* Footer: Clear + Apply */}
        <div css={footerStyle}>
          <Button
            size='small'
            variant='tertiary'
            onClick={handleClear}
            trackingId={PhoneSystemHistoryTrackingIds.tableFilters.userClearButton}
          >
            {t('Clear')}
          </Button>
          <Button
            size='small'
            onClick={handleApply}
            trackingId={PhoneSystemHistoryTrackingIds.tableFilters.userApplyButton}
          >
            {t('Apply')}
          </Button>
        </div>
      </PopoverDialog>
    </>
  );
}

// --- OptionCheckboxRow ---
// Pure visual component: Checkbox atom + Text in a clickable div.
// No useFormField overhead — follows the same pattern the design system's own Option
// component uses internally (static error/active/onBlur/onFocus values).
// Wrapped in React.memo so it only re-renders when its own props change.
type OptionCheckboxRowProps = {
  name: string;
  label: string;
  isChecked: boolean;
  isDisabled?: boolean;
  trackingId?: string;
  onToggle: () => void;
};

const NOOP = () => {};

const OptionCheckboxRow = memo(function OptionCheckboxRow({
  name,
  label,
  isChecked,
  isDisabled = false,
  trackingId,
  onToggle,
}: OptionCheckboxRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const showActive = isHovered && !isDisabled;

  return (
    <div
      role='checkbox'
      aria-checked={isChecked}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      onClick={isDisabled ? undefined : onToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={
        isDisabled
          ? undefined
          : (e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onToggle();
              }
            }
      }
      css={optionCheckboxRowStyle(isDisabled, showActive)}
    >
      <div css={{ pointerEvents: 'none', flexShrink: 0 }}>
        <Checkbox
          id={`user-filter-cb-${name}`}
          name={name}
          value={isChecked}
          error={false}
          active={showActive}
          disabled={isDisabled}
          aria-invalid={false}
          onChange={NOOP}
          onBlur={NOOP}
          onFocus={NOOP}
          trackingId={trackingId}
        />
      </div>
      {label}
    </div>
  );
});

// --- UserCheckboxRow ---
// Thin memo wrapper for office staff rows. Accepts a stable `toggleUserId` function
// and a `userId` (both stable), then constructs its own stable `onToggle` via
// useCallback — ensuring React.memo bails out whenever isChecked/isDisabled haven't changed.
type UserCheckboxRowProps = {
  name: string;
  label: string;
  userId: string;
  isChecked: boolean;
  isDisabled: boolean;
  toggleUserId: (userId: string) => void;
};

const UserCheckboxRow = memo(function UserCheckboxRow({
  name,
  label,
  userId,
  isChecked,
  isDisabled,
  toggleUserId,
}: UserCheckboxRowProps) {
  const handleToggle = useCallback(() => toggleUserId(userId), [toggleUserId, userId]);
  return (
    <OptionCheckboxRow
      name={name}
      label={label}
      isChecked={isChecked}
      isDisabled={isDisabled}
      trackingId={PhoneSystemHistoryTrackingIds.tableFilters.userOfficeStaffCheckbox}
      onToggle={handleToggle}
    />
  );
});

// --- Styles ---

const userFilterDialogStyle = css`
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 280px;
  max-height: 480px;
  overflow: hidden;
`;

const searchSectionStyle = css`
  padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-sm) 0;
  flex-shrink: 0;
`;

const scrollableBodyStyle = css`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: var(--fabric-space-padding-xs) 0;
`;

const weaveOptionsStyle = css`
  display: flex;
  flex-direction: column;
`;

const dividerStyle = css`
  border-bottom: 1px solid var(--colors-neutral-20);
  margin: var(--fabric-space-gap-xs) 0;
`;

const officeStaffHeaderStyle = css`
  padding: 0 var(--fabric-space-padding-sm);
  margin-bottom: var(--fabric-space-gap-xxs);
`;

const officeStaffCheckboxesStyle = css`
  display: flex;
  flex-direction: column;
  padding: 0 var(--fabric-space-padding-sm);
`;

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: var(--fabric-space-gap-sm);
  padding: var(--fabric-space-padding-sm);
  border-top: 1px solid var(--colors-neutral-20);
  flex-shrink: 0;
`;

const optionCheckboxRowStyle = (isDisabled: boolean, showActive: boolean) => css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
  padding: var(--fabric-space-padding-xs) var(--fabric-space-padding-sm);
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  user-select: none;
  background-color: ${showActive ? 'var(--colors-neutral-5)' : 'transparent'};
`;
